"use client";

import React, { useState } from "react";
import { toast } from "react-hot-toast";
import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/components/ui/card";
import { Button } from "@/app/components/ui/button";
import { Input } from "@/app/components/ui/input";
import { Label } from "@/app/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/app/components/ui/tabs";
import { Badge } from "@/app/components/ui/badge";
import { Progress } from "@/app/components/ui/progress";
import { Alert, AlertDescription } from "@/app/components/ui/alert";
import { 
  TrendingUp, 
  Users, 
  Music, 
  Play, 
  Heart,
  Calendar,
  BarChart3,
  Activity,
  Target,
  Zap,
  AlertCircle,
  CheckCircle,
  ExternalLink,
  Clock,
  Disc3,
  Mic,
  Headphones
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, BarChart, Bar, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent } from "@/app/components/ui/chart";

interface SpotifyData {
  profile: {
    id: string;
    name: string;
    type: 'user' | 'artist';
    followers: number;
    popularity?: number;
    genres?: string[];
    image?: string | null;
    external_urls?: {
      spotify?: string;
    };
  };
  analytics: {
    totalFollowers: number;
    totalAlbums?: number;
    totalTracks?: number;
    totalPlaylists?: number;
    totalFollowing?: number;
    popularity?: number;
    genres?: string[];
    topTracks?: Array<{
      name: string;
      artists: string;
      album: string;
      duration_ms: number;
      popularity: number;
      preview_url?: string | null;
      external_urls?: {
        spotify?: string;
      };
    }>;
    recentReleases?: Array<{
      name: string;
      release_date: string;
      total_tracks: number;
      album_type: string;
      images: Array<{
        url: string;
        height: number;
        width: number;
      }>;
      external_urls?: {
        spotify?: string;
      };
    }>;
    albums?: Array<{
      name: string;
      release_date: string;
      total_tracks: number;
      album_type: string;
      images: Array<{
        url: string;
        height: number;
        width: number;
      }>;
      external_urls?: {
        spotify?: string;
      };
    }>;
    singles?: Array<{
      name: string;
      release_date: string;
      total_tracks: number;
      album_type: string;
      images: Array<{
        url: string;
        height: number;
        width: number;
      }>;
      external_urls?: {
        spotify?: string;
      };
    }>;
    appearsOn?: Array<{
      name: string;
      release_date: string;
      total_tracks: number;
      album_type: string;
      images: Array<{
        url: string;
        height: number;
        width: number;
      }>;
      external_urls?: {
        spotify?: string;
      };
    }>;
    discoveredOn?: Array<{
      name: string;
      description: string;
      tracks: number;
      public: boolean;
      images: Array<{
        url: string;
        height: number;
        width: number;
      }>;
      external_urls?: {
        spotify?: string;
      };
    }>;
    featuring?: Array<{
      name: string;
      artists: string;
      album: string;
      duration_ms: number;
      popularity: number;
      preview_url?: string | null;
      external_urls?: {
        spotify?: string;
      };
    }>;
    relatedArtists?: Array<{
      name: string;
      followers: number;
      popularity: number;
      genres: string[];
      images: Array<{
        url: string;
        height: number;
        width: number;
      }>;
      external_urls?: {
        spotify?: string;
      };
    }>;
    topPlaylists?: Array<{
      name: string;
      description: string;
      tracks: number;
      public: boolean;
      collaborative: boolean;
      images: Array<{
        url: string;
        height: number;
        width: number;
      }>;
      external_urls?: {
        spotify?: string;
      };
    }>;
    followingArtists?: Array<{
      name: string;
      followers: number;
      popularity: number;
      genres: string[];
      images: Array<{
        url: string;
        height: number;
        width: number;
      }>;
      external_urls?: {
        spotify?: string;
      };
    }>;
  };
}

export default function SpotifyAnalytics() {
  const [spotifyId, setSpotifyId] = useState("");
  const [data, setData] = useState<SpotifyData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [concerts, setConcerts] = useState<any[]>([]);
  const [selectedConcert, setSelectedConcert] = useState<any>(null);
  const [location, setLocation] = useState("US");

  // TRPC mutations
  const analyzeSpotifyMutation = trpc.analytics.spotify.analyze.useMutation();
  const getConcertsMutation = trpc.analytics.spotify.getConcerts.useMutation();
  const getConcertMutation = trpc.analytics.spotify.getConcert.useMutation();

  // Format duration from milliseconds
  const formatDuration = (ms: number) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Format number with commas
  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  // Proxy Spotify images to avoid CORS issues
  const getProxiedImageUrl = (url: string) => {
    if (url.includes('i.scdn.co')) {
      // Use our own image proxy endpoint
      return `/api/proxy-image?url=${encodeURIComponent(url)}`;
    }
    return url;
  };

  // Handle image loading errors with a simple fallback
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const img = e.target as HTMLImageElement;
    // If the proxied image fails, try the original URL directly
    if (img.src.includes('/api/proxy-image')) {
      const urlParam = new URLSearchParams(img.src.split('?')[1]).get('url');
      if (urlParam) {
        img.src = urlParam;
      }
    }
  };

  const handleAnalyze = async () => {
    if (!spotifyId) return;
    
    setError(null);
    setData(null);

    try {
      const result = await analyzeSpotifyMutation.mutateAsync({ 
        id: spotifyId 
      });
      console.log("Spotify TRPC response:", result);
      
      // Ensure the profile type is properly typed
      const typedData = {
        ...result.data,
        profile: {
          ...result.data.profile,
          type: result.data.profile.type as 'user' | 'artist'
        }
      };
      
      setData(typedData);
      toast.success("Fetched Spotify analytics successfully", {
        icon: <CheckCircle className="h-4 w-4 text-green-600" />,
        duration: 3000,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    }
  };

  const handleGetConcerts = async () => {
    setError(null);
    try {
      const result = await getConcertsMutation.mutateAsync({
        gl: location,
      });
      
      if (result.success) {
        setConcerts(result.data.concerts || []);
        toast.success("Fetched concerts successfully", {
          icon: <CheckCircle className="h-4 w-4 text-green-600" />,
          duration: 3000,
        });
      } else {
        setError("Failed to fetch concerts");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    }
  };

  const handleGetConcert = async (concertId: string) => {
    setError(null);
    try {
      const result = await getConcertMutation.mutateAsync({
        id: concertId,
      });
      
      if (result.success) {
        setSelectedConcert(result.data);
        toast.success("Fetched concert details successfully", {
          icon: <CheckCircle className="h-4 w-4 text-green-600" />,
          duration: 3000,
        });
      } else {
        setError("Failed to fetch concert details");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <div className="mb-8">
        <h1 className="text-4xl font-bold tracking-tight mb-2">Spotify Analytics</h1>
        <p className="text-muted-foreground text-lg">
          Analyze Spotify user profiles and artist statistics using RapidAPI's Spotify23 API
        </p>
      </div>

      {/* Input Section */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Music className="h-5 w-5" />
            Spotify Profile Analysis
          </CardTitle>
          <CardDescription>
            Enter a Spotify user ID or artist ID to get detailed analytics. 
            Note: This requires a Spotify RapidAPI key to be configured.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <div className="flex-1">
              <Label htmlFor="spotify-id">Spotify User ID or Artist ID</Label>
              <Input
                id="spotify-id"
                placeholder="nocopyrightsounds or 2w9zwq3AktTeYYMuhMjju8"
                value={spotifyId}
                onChange={(e) => setSpotifyId(e.target.value)}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Enter a Spotify username (like "nocopyrightsounds") or artist ID (like "2w9zwq3AktTeYYMuhMjju8")
              </p>
            </div>
            <div className="flex items-end">
              <Button 
                onClick={handleAnalyze} 
                disabled={!spotifyId || analyzeSpotifyMutation.isPending}
                className="min-w-[120px]"
              >
                {analyzeSpotifyMutation.isPending ? (
                  <>
                    <Activity className="h-4 w-4 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Zap className="h-4 w-4 mr-2" />
                    Analyze
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error Display */}
      {error && (
        <Alert className="mb-8" variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Results */}
      {data && (
        <div className="space-y-8">
          {/* Profile Overview */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Headphones className="h-5 w-5" />
                Profile Overview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-start gap-6">
                {data.profile.image && (
                  <img
                    src={getProxiedImageUrl(data.profile.image)}
                    alt={data.profile.name}
                    className="w-24 h-24 rounded-full object-cover"
                    onError={handleImageError}
                  />
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h2 className="text-2xl font-bold">{data.profile.name}</h2>
                    <Badge variant={data.profile.type === 'artist' ? 'default' : 'secondary'}>
                      {data.profile.type === 'artist' ? 'Artist' : 'User'}
                    </Badge>
                    {data.profile.external_urls?.spotify && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(data.profile.external_urls?.spotify, '_blank')}
                      >
                        <ExternalLink className="h-4 w-4 mr-1" />
                        Open in Spotify
                      </Button>
                    )}
                  </div>
                  <p className="text-muted-foreground mb-4">ID: {data.profile.id}</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-blue-500" />
                      <span className="text-sm font-medium">Followers:</span>
                      <span className="font-bold">{formatNumber(data.analytics.totalFollowers)}</span>
                    </div>
                    
                    {data.profile.type === 'artist' && (
                      <>
                        <div className="flex items-center gap-2">
                          <Disc3 className="h-4 w-4 text-green-500" />
                          <span className="text-sm font-medium">Albums:</span>
                          <span className="font-bold">{data.analytics.totalAlbums || 0}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Music className="h-4 w-4 text-purple-500" />
                          <span className="text-sm font-medium">Tracks:</span>
                          <span className="font-bold">{data.analytics.totalTracks || 0}</span>
                        </div>
                      </>
                    )}
                    
                    {data.profile.type === 'user' && (
                      <>
                        <div className="flex items-center gap-2">
                          <Play className="h-4 w-4 text-orange-500" />
                          <span className="text-sm font-medium">Playlists:</span>
                          <span className="font-bold">{data.analytics.totalPlaylists || 0}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Mic className="h-4 w-4 text-red-500" />
                          <span className="text-sm font-medium">Following:</span>
                          <span className="font-bold">{data.analytics.totalFollowing || 0}</span>
                        </div>
                      </>
                    )}
                  </div>

                  {data.profile.popularity && (
                    <div className="mt-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium">Popularity</span>
                        <span className="text-sm text-muted-foreground">{data.profile.popularity}/100</span>
                      </div>
                      <Progress value={data.profile.popularity} className="h-2" />
                    </div>
                  )}

                  {data.profile.genres && data.profile.genres.length > 0 && (
                    <div className="mt-4">
                      <span className="text-sm font-medium mb-2 block">Genres:</span>
                      <div className="flex flex-wrap gap-2">
                        {data.profile.genres.map((genre, index) => (
                          <Badge key={index} variant="outline">
                            {genre}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Analytics Tabs */}
          <Tabs defaultValue={data.profile.type === 'artist' ? 'tracks' : 'playlists'} className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value={data.profile.type === 'artist' ? 'tracks' : 'playlists'}>
                {data.profile.type === 'artist' ? 'Top Tracks' : 'Playlists'}
              </TabsTrigger>
              <TabsTrigger value={data.profile.type === 'artist' ? 'albums' : 'artists'}>
                {data.profile.type === 'artist' ? 'Albums & Singles' : 'Following Artists'}
              </TabsTrigger>
              <TabsTrigger value={data.profile.type === 'artist' ? 'related' : 'artists'}>
                {data.profile.type === 'artist' ? 'Related & More' : 'Following Artists'}
              </TabsTrigger>
            </TabsList>

            {data.profile.type === 'artist' ? (
              <>
                {/* Artist - Top Tracks */}
                <TabsContent value="tracks">
                  <Card>
                    <CardHeader>
                      <CardTitle>Top Tracks</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {data.analytics.topTracks?.map((track, index) => (
                          <div key={index} className="flex items-center gap-4 p-4 border rounded-lg">
                            <div className="shrink-0 w-8 text-center font-bold text-muted-foreground">
                              {index + 1}
                            </div>
                            <div className="flex-1">
                              <h3 className="font-semibold">{track.name}</h3>
                              <p className="text-sm text-muted-foreground">{track.artists}</p>
                              <p className="text-xs text-muted-foreground">{track.album}</p>
                            </div>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground">
                              <div className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {formatDuration(track.duration_ms)}
                              </div>
                              <div className="flex items-center gap-1">
                                <TrendingUp className="h-3 w-3" />
                                {track.popularity}/100
                              </div>
                              {track.external_urls?.spotify && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => window.open(track.external_urls?.spotify, '_blank')}
                                >
                                  <ExternalLink className="h-3 w-3" />
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Artist - Albums & Singles */}
                <TabsContent value="albums">
                  <div className="space-y-6">
                    {/* Albums */}
                    {data.analytics.albums && data.analytics.albums.length > 0 && (
                      <Card>
                        <CardHeader>
                          <CardTitle>Albums</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {data.analytics.albums.map((album, index) => (
                              <div key={index} className="border rounded-lg p-4">
                                {album.images.length > 0 && (
                                  <img
                                    src={getProxiedImageUrl(album.images[0].url)}
                                    alt={album.name}
                                    className="w-full h-32 object-cover rounded mb-3"
                                    onError={handleImageError}
                                  />
                                )}
                                <h3 className="font-semibold mb-1">{album.name}</h3>
                                <p className="text-sm text-muted-foreground mb-2">
                                  {new Date(album.release_date).toLocaleDateString()}
                                </p>
                                <div className="flex items-center justify-between text-xs text-muted-foreground">
                                  <span>{album.total_tracks} tracks</span>
                                  <Badge variant="outline">{album.album_type}</Badge>
                                </div>
                                {album.external_urls?.spotify && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="w-full mt-3"
                                    onClick={() => window.open(album.external_urls?.spotify, '_blank')}
                                  >
                                    <ExternalLink className="h-3 w-3 mr-1" />
                                    Open in Spotify
                                  </Button>
                                )}
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Singles */}
                    {data.analytics.singles && data.analytics.singles.length > 0 && (
                      <Card>
                        <CardHeader>
                          <CardTitle>Singles & EPs</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {data.analytics.singles.map((single, index) => (
                              <div key={index} className="border rounded-lg p-4">
                                {single.images.length > 0 && (
                                  <img
                                    src={getProxiedImageUrl(single.images[0].url)}
                                    alt={single.name}
                                    className="w-full h-32 object-cover rounded mb-3"
                                    onError={handleImageError}
                                  />
                                )}
                                <h3 className="font-semibold mb-1">{single.name}</h3>
                                <p className="text-sm text-muted-foreground mb-2">
                                  {new Date(single.release_date).toLocaleDateString()}
                                </p>
                                <div className="flex items-center justify-between text-xs text-muted-foreground">
                                  <span>{single.total_tracks} tracks</span>
                                  <Badge variant="outline">{single.album_type}</Badge>
                                </div>
                                {single.external_urls?.spotify && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="w-full mt-3"
                                    onClick={() => window.open(single.external_urls?.spotify, '_blank')}
                                  >
                                    <ExternalLink className="h-3 w-3 mr-1" />
                                    Open in Spotify
                                  </Button>
                                )}
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                </TabsContent>

                {/* Artist - Related & More */}
                <TabsContent value="related">
                  <div className="space-y-6">
                    {/* Related Artists */}
                    {data.analytics.relatedArtists && data.analytics.relatedArtists.length > 0 && (
                      <Card>
                        <CardHeader>
                          <CardTitle>Related Artists</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {data.analytics.relatedArtists.map((artist, index) => (
                              <div key={index} className="border rounded-lg p-4">
                                {artist.images.length > 0 && (
                                  <img
                                    src={getProxiedImageUrl(artist.images[0].url)}
                                    alt={artist.name}
                                    className="w-full h-32 object-cover rounded mb-3"
                                    onError={handleImageError}
                                  />
                                )}
                                <h3 className="font-semibold mb-1">{artist.name}</h3>
                                <p className="text-sm text-muted-foreground mb-2">
                                  {formatNumber(artist.followers)} followers
                                </p>
                                <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
                                  <span>Popularity: {artist.popularity}/100</span>
                                </div>
                                {artist.genres.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mb-3">
                                    {artist.genres.slice(0, 2).map((genre, genreIndex) => (
                                      <Badge key={genreIndex} variant="outline" className="text-xs">
                                        {genre}
                                      </Badge>
                                    ))}
                                  </div>
                                )}
                                {artist.external_urls?.spotify && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="w-full"
                                    onClick={() => window.open(artist.external_urls?.spotify, '_blank')}
                                  >
                                    <ExternalLink className="h-3 w-3 mr-1" />
                                    Open in Spotify
                                  </Button>
                                )}
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Appears On */}
                    {data.analytics.appearsOn && data.analytics.appearsOn.length > 0 && (
                      <Card>
                        <CardHeader>
                          <CardTitle>Appears On</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {data.analytics.appearsOn.map((album, index) => (
                              <div key={index} className="border rounded-lg p-4">
                                {album.images.length > 0 && (
                                  <img
                                    src={getProxiedImageUrl(album.images[0].url)}
                                    alt={album.name}
                                    className="w-full h-32 object-cover rounded mb-3"
                                    onError={handleImageError}
                                  />
                                )}
                                <h3 className="font-semibold mb-1">{album.name}</h3>
                                <p className="text-sm text-muted-foreground mb-2">
                                  {new Date(album.release_date).toLocaleDateString()}
                                </p>
                                <div className="flex items-center justify-between text-xs text-muted-foreground">
                                  <span>{album.total_tracks} tracks</span>
                                  <Badge variant="outline">{album.album_type}</Badge>
                                </div>
                                {album.external_urls?.spotify && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="w-full mt-3"
                                    onClick={() => window.open(album.external_urls?.spotify, '_blank')}
                                  >
                                    <ExternalLink className="h-3 w-3 mr-1" />
                                    Open in Spotify
                                  </Button>
                                )}
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Featuring */}
                    {data.analytics.featuring && data.analytics.featuring.length > 0 && (
                      <Card>
                        <CardHeader>
                          <CardTitle>Featuring</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-4">
                            {data.analytics.featuring.map((track, index) => (
                              <div key={index} className="flex items-center gap-4 p-4 border rounded-lg">
                                <div className="shrink-0 w-8 text-center font-bold text-muted-foreground">
                                  {index + 1}
                                </div>
                                <div className="flex-1">
                                  <h3 className="font-semibold">{track.name}</h3>
                                  <p className="text-sm text-muted-foreground">{track.artists}</p>
                                  <p className="text-xs text-muted-foreground">{track.album}</p>
                                </div>
                                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                  <div className="flex items-center gap-1">
                                    <Clock className="h-3 w-3" />
                                    {formatDuration(track.duration_ms)}
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <TrendingUp className="h-3 w-3" />
                                    {track.popularity}/100
                                  </div>
                                  {track.external_urls?.spotify && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => window.open(track.external_urls?.spotify, '_blank')}
                                    >
                                      <ExternalLink className="h-3 w-3" />
                                    </Button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Discovered On */}
                    {data.analytics.discoveredOn && data.analytics.discoveredOn.length > 0 && (
                      <Card>
                        <CardHeader>
                          <CardTitle>Discovered On</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-4">
                            {data.analytics.discoveredOn.map((playlist, index) => (
                              <div key={index} className="flex items-center gap-4 p-4 border rounded-lg">
                                {playlist.images.length > 0 && (
                                  <img
                                    src={getProxiedImageUrl(playlist.images[0].url)}
                                    alt={playlist.name}
                                    className="w-16 h-16 object-cover rounded"
                                    onError={handleImageError}
                                  />
                                )}
                                <div className="flex-1">
                                  <h3 className="font-semibold">{playlist.name}</h3>
                                  <p className="text-sm text-muted-foreground">{playlist.description}</p>
                                  <div className="flex items-center gap-2 mt-1">
                                    <span className="text-xs text-muted-foreground">
                                      {playlist.tracks} tracks
                                    </span>
                                    {playlist.public && (
                                      <Badge variant="outline" className="text-xs">Public</Badge>
                                    )}
                                  </div>
                                </div>
                                {playlist.external_urls?.spotify && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => window.open(playlist.external_urls?.spotify, '_blank')}
                                  >
                                    <ExternalLink className="h-3 w-3 mr-1" />
                                    Open
                                  </Button>
                                )}
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                </TabsContent>
              </>
            ) : (
              <>
                {/* User - Playlists */}
                <TabsContent value="playlists">
                  <Card>
                    <CardHeader>
                      <CardTitle>Top Playlists</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {data.analytics.topPlaylists?.map((playlist, index) => (
                          <div key={index} className="flex items-center gap-4 p-4 border rounded-lg">
                            {playlist.images.length > 0 && (
                              <img
                                src={playlist.images[0].url}
                                alt={playlist.name}
                                className="w-16 h-16 object-cover rounded"
                              />
                            )}
                            <div className="flex-1">
                              <h3 className="font-semibold">{playlist.name}</h3>
                              <p className="text-sm text-muted-foreground">{playlist.description}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-xs text-muted-foreground">
                                  {playlist.tracks} tracks
                                </span>
                                {playlist.public && (
                                  <Badge variant="outline" className="text-xs">Public</Badge>
                                )}
                                {playlist.collaborative && (
                                  <Badge variant="outline" className="text-xs">Collaborative</Badge>
                                )}
                              </div>
                            </div>
                            {playlist.external_urls?.spotify && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => window.open(playlist.external_urls?.spotify, '_blank')}
                              >
                                <ExternalLink className="h-3 w-3 mr-1" />
                                Open
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* User - Following Artists */}
                <TabsContent value="artists">
                  <Card>
                    <CardHeader>
                      <CardTitle>Following Artists</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {data.analytics.followingArtists?.map((artist, index) => (
                          <div key={index} className="border rounded-lg p-4">
                            {artist.images.length > 0 && (
                              <img
                                src={artist.images[0].url}
                                alt={artist.name}
                                className="w-full h-32 object-cover rounded mb-3"
                              />
                            )}
                            <h3 className="font-semibold mb-1">{artist.name}</h3>
                            <p className="text-sm text-muted-foreground mb-2">
                              {formatNumber(artist.followers)} followers
                            </p>
                            <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
                              <span>Popularity: {artist.popularity}/100</span>
                            </div>
                            {artist.genres.length > 0 && (
                              <div className="flex flex-wrap gap-1 mb-3">
                                {artist.genres.slice(0, 2).map((genre, genreIndex) => (
                                  <Badge key={genreIndex} variant="outline" className="text-xs">
                                    {genre}
                                  </Badge>
                                ))}
                              </div>
                            )}
                            {artist.external_urls?.spotify && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="w-full"
                                onClick={() => window.open(artist.external_urls?.spotify, '_blank')}
                              >
                                <ExternalLink className="h-3 w-3 mr-1" />
                                Open in Spotify
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </>
            )}
          </Tabs>
        </div>
      )}

      {/* Concerts Section */}
      <div className="mt-12">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Concerts & Events
            </CardTitle>
            <CardDescription>
              Discover upcoming concerts and events by location
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex gap-4 items-end">
                <div className="flex-1">
                  <Label htmlFor="location">Location (Country Code)</Label>
                  <Input
                    id="location"
                    placeholder="US, GB, CA, etc."
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <Button
                  onClick={handleGetConcerts}
                  disabled={getConcertsMutation.isPending}
                  className="min-w-[120px]"
                >
                  {getConcertsMutation.isPending ? (
                    <>
                      <Activity className="h-4 w-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <Calendar className="h-4 w-4 mr-2" />
                      Get Concerts
                    </>
                  )}
                </Button>
              </div>

              {concerts.length > 0 && (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {concerts.slice(0, 12).map((concert: any, index: number) => (
                    <Card key={index} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="space-y-3">
                          {concert.images && concert.images[0] && (
                            <img
                              src={getProxiedImageUrl(concert.images[0].url)}
                              alt={concert.name}
                              className="w-full h-32 object-cover rounded"
                              onError={handleImageError}
                            />
                          )}
                          <div>
                            <h3 className="font-semibold text-sm line-clamp-2">{concert.name}</h3>
                            <p className="text-xs text-muted-foreground mt-1">
                              {concert.venue?.name && `${concert.venue.name} • `}
                              {concert.venue?.city && `${concert.venue.city}, `}
                              {concert.venue?.country}
                            </p>
                            {concert.date && (
                              <p className="text-xs text-muted-foreground mt-1">
                                {new Date(concert.date).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                          {concert.id && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleGetConcert(concert.id)}
                              disabled={getConcertMutation.isPending}
                              className="w-full"
                            >
                              View Details
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {selectedConcert && (
                <Card className="mt-6">
                  <CardHeader>
                    <CardTitle>Concert Details</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-start gap-4">
                        {selectedConcert.images && selectedConcert.images[0] && (
                          <img
                            src={getProxiedImageUrl(selectedConcert.images[0].url)}
                            alt={selectedConcert.name}
                            className="w-24 h-24 object-cover rounded"
                            onError={handleImageError}
                          />
                        )}
                        <div className="flex-1">
                          <h3 className="text-xl font-bold">{selectedConcert.name}</h3>
                          {selectedConcert.venue && (
                            <div className="mt-2 space-y-1">
                              <p className="font-medium">{selectedConcert.venue.name}</p>
                              <p className="text-sm text-muted-foreground">
                                {selectedConcert.venue.city}, {selectedConcert.venue.country}
                              </p>
                              {selectedConcert.venue.address && (
                                <p className="text-sm text-muted-foreground">
                                  {selectedConcert.venue.address}
                                </p>
                              )}
                            </div>
                          )}
                          {selectedConcert.date && (
                            <p className="text-sm text-muted-foreground mt-2">
                              {new Date(selectedConcert.date).toLocaleDateString('en-US', {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      {selectedConcert.artists && selectedConcert.artists.length > 0 && (
                        <div>
                          <h4 className="font-semibold mb-2">Artists</h4>
                          <div className="flex flex-wrap gap-2">
                            {selectedConcert.artists.map((artist: any, index: number) => (
                              <Badge key={index} variant="secondary">
                                {artist.name}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {selectedConcert.external_urls && (
                        <div className="flex gap-2">
                          {selectedConcert.external_urls.spotify && (
                            <Button asChild size="sm">
                              <a
                                href={selectedConcert.external_urls.spotify}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <Music className="h-4 w-4 mr-2" />
                                View on Spotify
                              </a>
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
